/**
 * Spec 0062 (saas-auth SASATH-0079) — the product → OIDC-clients reverse view.
 *
 * SUPER_ADMIN-only section mounted on the product detail screen (the caller in
 * ProductDetail gates rendering on `operator?.isSuperAdmin`; this component
 * additionally degrades to a contained "SUPER_ADMIN required" state if the API
 * returns 403). Self-fetches the client list on mount with isolated
 * loading/empty/error states — it is NOT folded into the shared
 * productDetailLoader, so an ordinary SAAS_ADMIN viewer's product page is never
 * broken by a 403 here.
 *
 * A product can be in scope for many OIDC clients. Each client is tagged with
 * HOW it's in scope:
 *   - PRODUCT  → this product is explicitly on the client's allow-list; a Remove
 *     action detaches it via the SASADM-0061 `allowed-products` DELETE (reused —
 *     the same allow-list row the client's own edit screen manages).
 *   - PLATFORM → the client is platform-scoped (all products); it covers this
 *     product globally, so the row is READ-ONLY with an "All products" badge and
 *     NO remove control (honors `removableFromProduct:false`). To stop it
 *     covering a product the admin changes the CLIENT's access scope on the
 *     client's page.
 * A PLATFORM-row DELETE returns 409 CLIENT_IS_PLATFORM_SCOPED — prevented in the
 * UI, and handled defensively (a dedicated message) if it still occurs.
 */
import {
  Alert,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useState } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { ProductOidcClientDto } from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';

function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

export function ProductOidcClients({ productId }: { productId: number }) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [rows, setRows] = useState<ProductOidcClientDto[] | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<ProductOidcClientDto | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setLoadError(null);
    try {
      const list = await apiAdmin.products.oidcClients(productId);
      setRows(list);
      setForbidden(false);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setRows([]);
        return;
      }
      if (err instanceof ApiError) setLoadError(err);
      setRows([]);
    }
  }

  useEffect(() => {
    // `refresh()` only setStates after an `await`; wrap it in an inline async
    // IIFE so the effect body holds no synchronous setState (behavior identical).
    void (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  async function handleRemove() {
    if (!confirmRemove) return;
    setBusy(true);
    try {
      // Reuse the SASADM-0061 allow-list DELETE (same row the client's own edit
      // screen manages) — no separate remove endpoint on this reverse view.
      await apiAdmin.oidcClients.removeAllowedProduct(confirmRemove.clientDbId, productId);
      snack.success(t('productOidcClients.removed', 'OIDC client removed from product.'));
      setConfirmRemove(null);
      await refresh();
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setConfirmRemove(null);
        return;
      }
      if (err instanceof ApiError) {
        // Defensive: a PLATFORM-scoped client can't be detached here (shouldn't
        // occur — the row offers no remove control). Surface the dedicated
        // guidance; otherwise the backend's own message/code.
        const message =
          err.code === 'CLIENT_IS_PLATFORM_SCOPED'
            ? t(
                'productOidcClients.platformScopedError',
                "This client applies to all products; change its access scope on the client's page to remove it.",
              )
            : (err.details.message ?? t(`errors.codes.${err.code}`, err.code));
        snack.error(message);
      }
      setConfirmRemove(null);
    } finally {
      setBusy(false);
    }
  }

  if (forbidden) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('productOidcClients.title', 'OIDC clients')}
        </Typography>
        <Alert severity="warning">
          {t('productOidcClients.superAdminRequired', 'Super admin required to manage OIDC clients.')}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="h3" gutterBottom>
        {t('productOidcClients.title', 'OIDC clients')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2,
        }}
      >
        {t(
          'productOidcClients.explanation',
          "The OIDC clients in scope for this product. Product-scoped clients can be removed here; platform-scoped clients apply to all products and are managed on the client's own page.",
        )}
      </Typography>
      <ErrorBanner error={loadError} />
      {rows === null || rows === undefined ? (
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('common.loading', 'Loading')}
        </Typography>
      ) : rows.length === 0 ? (
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('productOidcClients.empty', 'No OIDC clients.')}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('productOidcClients.columns.name', 'Name')}</TableCell>
                <TableCell>{t('productOidcClients.columns.clientId', 'Client ID')}</TableCell>
                <TableCell>{t('productOidcClients.columns.scope', 'Scope')}</TableCell>
                <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((client) => {
                const platform = client.scope === 'PLATFORM';
                return (
                  <TableRow key={client.clientDbId}>
                    <TableCell>{client.clientName}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                        }}
                      >
                        {client.clientId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Chip
                          label={
                            platform
                              ? t('productOidcClients.scopePlatform', 'Platform')
                              : t('productOidcClients.scopeProduct', 'Product')
                          }
                          size="small"
                          color={platform ? 'default' : 'primary'}
                          variant={platform ? 'outlined' : 'filled'}
                        />
                        {platform ? (
                          <Chip
                            label={t('productOidcClients.allProductsBadge', 'All products')}
                            size="small"
                            variant="outlined"
                          />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {/* PRODUCT rows are removable; PLATFORM rows are read-only
                          (honors `removableFromProduct:false`) — no control. */}
                      {client.removableFromProduct && !platform ? (
                        <IconButton
                          aria-label={t('productOidcClients.remove', 'Remove')}
                          title={t('productOidcClients.remove', 'Remove')}
                          onClick={() => setConfirmRemove(client)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ConfirmDialog
        open={Boolean(confirmRemove)}
        title={t('productOidcClients.removeTitle', 'Remove OIDC client?')}
        body={t(
          'productOidcClients.removeBody',
          'Remove {{name}} from this product? Its tenants will no longer be able to use this client (unless another product still allows it).',
          { name: confirmRemove?.clientName ?? '' },
        )}
        severity="error"
        confirmLabel={t('productOidcClients.remove', 'Remove')}
        busy={busy}
        onConfirm={() => void handleRemove()}
        onCancel={() => setConfirmRemove(null)}
      />
    </Paper>
  );
}
